import React, { useCallback, useMemo, useState } from "react";
import { DropdownButton, toast } from "@jsq/ds/core";
import {
  useGenerateFatcaCrsReportMutation,
  useGenerateScreeningStatusReportMutation,
} from "~/graphql/supergraphHooks";
import {
  useComplianceFatcaCrsConfigMigration,
  useComplianceFatcaCrsFeatureFlag,
} from "~/pages/Compliance/flags";
import { getGeneratingModalToastText } from "../../ExportActions/utils";
import {
  KeyOfPositionExportActions,
  PositionExportActions,
} from "../../ExportActions/utils/constants";
import { GeneratingModal } from "../../GeneratingModal";
import { POSITIONS_CSV_HEADERS } from "../constant";
import { PositionsTableRowItem } from "../types";
import {
  generatePositionsCSV,
  downloadCSVFile,
  formatExportFilename,
} from "../util/exportUtil";
import { buildScreeningStatusReportInput } from "./utils";

interface ExportButtonProps {
  selectedRows: PositionsTableRowItem[];
  onRowsDeselected: () => void;
}

const ExportButton: React.FC<ExportButtonProps> = ({ selectedRows, onRowsDeselected }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [downloadReportJobId, setDownloadReportJobId] = useState(-1);
  const [currentReportTypeSelected, setCurrentReportTypeSelected] = useState(
    "" as KeyOfPositionExportActions,
  );

  const [generateScreeningStatusReport] = useGenerateScreeningStatusReportMutation();
  const [generateFatcaCrsReport] = useGenerateFatcaCrsReportMutation();
  const isMigration = useComplianceFatcaCrsConfigMigration();
  const isLegacyFlag = useComplianceFatcaCrsFeatureFlag();
  const isFatcaCrsEnabled = isMigration || isLegacyFlag;

  const isDisabled: boolean = selectedRows?.length === 0;
  const showFatcaCrsExport = useMemo(
    () => isFatcaCrsEnabled && selectedRows.some((row) => row.onboardingStatus !== null),
    [isFatcaCrsEnabled, selectedRows],
  );
  const exportActions = useMemo(() => {
    if (showFatcaCrsExport) {
      return PositionExportActions;
    }

    const { FatcaCrs: _fatcaCrs, ...actionsWithoutFatcaCrs } = PositionExportActions;
    return actionsWithoutFatcaCrs;
  }, [showFatcaCrsExport]);

  const closeGeneratingModal = useCallback(() => {
    onRowsDeselected();
    setShowGeneratingModal(false);
    setDownloadReportJobId(-1);
  }, [onRowsDeselected]);

  const onDownloadError = useCallback((reportType: KeyOfPositionExportActions) => {
    toast.error(
      `An error occurred while generating the ${getGeneratingModalToastText(reportType)} report.`,
    );
  }, []);

  const handlePositionInformationReport = useCallback(() => {
    setIsExporting(true);

    try {
      const csvContent = generatePositionsCSV(selectedRows, POSITIONS_CSV_HEADERS);
      const filename = formatExportFilename();
      downloadCSVFile(csvContent, filename);

      toast.success(`Exported ${selectedRows.length} position(s) successfully`);
    } catch {
      toast.error("Failed to export positions.");
    } finally {
      setIsExporting(false);
      onRowsDeselected();
    }
  }, [onRowsDeselected, selectedRows]);

  const handleScreeningStatusReport = useCallback(async () => {
    setCurrentReportTypeSelected("ScreeningStatus");
    setDownloadReportJobId(-1);
    setShowGeneratingModal(true);

    const reportInput = buildScreeningStatusReportInput(selectedRows);

    try {
      const { data } = await generateScreeningStatusReport({
        variables: {
          reportInput,
        },
      });

      if (data?.createScreeningReportForPositions?.status === "error") {
        onDownloadError("ScreeningStatus");
        closeGeneratingModal();
      } else {
        const jobId = data?.createScreeningReportForPositions?.id;
        if (jobId !== undefined) {
          setDownloadReportJobId(jobId);
        }
      }
    } catch {
      onDownloadError("ScreeningStatus");
      closeGeneratingModal();
    }
  }, [generateScreeningStatusReport, selectedRows, closeGeneratingModal, onDownloadError]);

  const handleFatcaCrsReport = useCallback(async () => {
    setCurrentReportTypeSelected("FatcaCrs");
    setDownloadReportJobId(-1);
    setShowGeneratingModal(true);

    try {
      const { data } = await generateFatcaCrsReport({
        variables: {
          positionIds: selectedRows.map((row) => String(row.id)),
        },
      });

      if (data?.createFatcaCrsReportForPositions?.status === "error") {
        onDownloadError("FatcaCrs");
        closeGeneratingModal();
      } else {
        const jobId = data?.createFatcaCrsReportForPositions?.id;
        if (jobId !== undefined) {
          setDownloadReportJobId(jobId);
        }
      }
    } catch {
      onDownloadError("FatcaCrs");
      closeGeneratingModal();
    }
  }, [generateFatcaCrsReport, selectedRows, closeGeneratingModal, onDownloadError]);

  const handleExportActions = useCallback(
    (actionType: KeyOfPositionExportActions) => {
      if (actionType === "PositionInformation") {
        handlePositionInformationReport();
      } else if (actionType === "ScreeningStatus") {
        handleScreeningStatusReport().catch(() => {
          onDownloadError("ScreeningStatus");
          closeGeneratingModal();
        });
      } else if (actionType === "FatcaCrs") {
        handleFatcaCrsReport().catch(() => {
          onDownloadError("FatcaCrs");
          closeGeneratingModal();
        });
      }
    },
    [
      closeGeneratingModal,
      handleFatcaCrsReport,
      handlePositionInformationReport,
      handleScreeningStatusReport,
      onDownloadError,
    ],
  );

  return (
    <>
      <DropdownButton
        label="Export"
        variant="primary"
        align="right"
        isLoading={isExporting}
        disabled={isDisabled}
        icon="caret-down"
      >
        {Object.entries(exportActions).map(([key, value]) => (
          <DropdownButton.Action
            key={key}
            onSelect={() => handleExportActions(key as KeyOfPositionExportActions)}
          >
            {value}
          </DropdownButton.Action>
        ))}
      </DropdownButton>

      {showGeneratingModal && (
        <GeneratingModal
          showGeneratingModal={showGeneratingModal}
          closeGeneratingModal={closeGeneratingModal}
          jobId={downloadReportJobId}
          reportType={currentReportTypeSelected}
        />
      )}
    </>
  );
};

export default ExportButton;
